import React, { useMemo, useCallback, useState } from "react";
import { type LineSearchDropdownProps } from "@bloomberg/media-ui-dropdown";
import { useLocalStorage } from "@foundation/local-storage";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import typographyStyles from "@bloomberg/media-ui-tokens/css/typography.module.css";
import { createClasses } from "@bloomberg/media-ui-core";
import styles from "./styles.module.css";
import { ChartLegendSection } from "~components/BenchmarkComparison/ChartLegendSection";
import { SearchControls } from "~components/BenchmarkComparison/SearchControls";
import { TimeRangeTabs } from "~components/BenchmarkComparison/TimeRangeTabs";
import { getChartTickerColor } from "~components/WatchlistComparisonChart/utils";
import { useBenchmarkComparisonData } from "~hooks/useBenchmarkComparisonData";
import { addPageAction } from "~utils/newrelic/newrelicBrowser";
import { TranslatedMessage } from "~utils/i18n";
import { BENCHMARK_CHART_TIME_RANGES } from "~constants";

type ChartTimeRangeType = (typeof BENCHMARK_CHART_TIME_RANGES)[number];
const MAPPED_TAB_INDEX_TO_CHART_TIME_RANGE: Record<number, ChartTimeRangeType> =
  {
    0: "1M",
    1: "6M",
    2: "YTD",
    3: "1Y",
    4: "5Y",
  };

const MAX_COMPARISONS_ALLOWED = 5;
const LOCAL_STORAGE_KEY = "compareBenchmarkChartTabs";

type DropdownOptions = LineSearchDropdownProps["options"];

export default function BenchmarkComparison({
  defaultTickers = [],
}: {
  defaultTickers?: Array<string>;
}) {
  const [selectedTab, setSelectedTab] = useLocalStorage(
    LOCAL_STORAGE_KEY,
    "1M"
  );

  // State to handle what is visible on the chart. This state is updated when a user toggles a ticker legend
  const [selectedTickers, setSelectedTickers] = useState<{
    [key: string]: boolean;
  }>(defaultTickers.reduce((acc, ticker) => ({ ...acc, [ticker]: true }), {}));

  // Derive list of tickers from checked tickers mapping
  const tickersArr = useMemo(
    () => Object.keys(selectedTickers),
    [selectedTickers]
  );

  const {
    data: chartData = [],
    status,
    refetch,
  } = useBenchmarkComparisonData(tickersArr, selectedTab);

  const { tickerChartData, tickerLegends } = useMemo(() => {
    const data = chartData.filter((d) => selectedTickers[d.id]);

    const legends = chartData.map((d, i) => ({
      id: d.id,
      name: d.id,
      color: getChartTickerColor(i),
      checked: Boolean(selectedTickers[d.id]),
    }));

    return { tickerChartData: data, tickerLegends: legends };
  }, [chartData, selectedTickers]);

  const handleTimeRangeChange = useCallback(
    (_tabText: string, index?: number) => {
      if (index === undefined) return;
      // Use tab index as key to map to chart time range selection.
      // Tab text changes due to language locales
      const chartTimeRange = MAPPED_TAB_INDEX_TO_CHART_TIME_RANGE[index];

      setSelectedTab(chartTimeRange);
      addPageAction("benchmarkComparisonTimeRangeChange", {
        timeRange: chartTimeRange,
      });
    },
    [setSelectedTab]
  );

  const handleAddSymbol = useCallback(
    (
      option: DropdownOptions[0] | null,
      downshiftHandle: { setState: (value: unknown) => void }
    ) => {
      if (!option?.value) return;

      const newTickerId = option.value;

      trackClickEvent(
        "quote_add_compare",
        "add-compare-from-db",
        "click",
        "Compare to Benchmarks Module"
      );

      trackClickEvent(
        "quote_add_compared_security",
        newTickerId,
        "click",
        "Compare to Benchmarks Module"
      );

      setSelectedTickers((prevState) => ({
        ...prevState,
        [newTickerId]: true,
      }));

      const state = {
        inputValue: "",
        selectedItem: { subText: "", value: "", optionId: "" },
      };

      // workaround to clear text box value after selecting
      downshiftHandle.setState(state);

      addPageAction("benchmarkComparisonAddTicker", {
        ticker: newTickerId,
      });
    },
    [setSelectedTickers]
  );

  const handleTickerSelectionChange = useCallback(
    (tickerId: string, isChecked: boolean) => {
      if (isChecked) {
        trackClickEvent(
          "quote_add_compare",
          "add-compare",
          "click",
          "Compare to Benchmarks Module"
        );
      } else {
        trackClickEvent(
          "quote_remove_compare",
          "remove-compare",
          "click",
          "Compare to Benchmarks Module"
        );
      }

      setSelectedTickers((prevState) => ({
        ...prevState,
        [tickerId]: isChecked,
      }));
    },
    [setSelectedTickers]
  );

  const handleTickerRemoval = useCallback(
    (itemId: string) => {
      trackClickEvent(
        "quote_remove_compare",
        "remove-compare-from-db",
        "click",
        "Compare to Benchmarks Module"
      );

      setSelectedTickers((prev) => {
        const { [itemId]: _, ...rest } = prev;

        return rest;
      });

      addPageAction("benchmarkComparisonRemoveTicker", {
        ticker: itemId,
      });
    },
    [setSelectedTickers]
  );

  return (
    <>
      <div
        className={createClasses([
          styles.sectionTitle,
          typographyStyles.coreHeadlineSm,
        ])}
      >
        <TranslatedMessage
          id="compareToBenchmarks"
          defaultMessage="Compare to Benchmarks"
        />
      </div>
      <div className={styles.chartContainer}>
        <div className={styles.chartControls}>
          <SearchControls
            disabled={tickersArr.length >= MAX_COMPARISONS_ALLOWED}
            onAddTicker={handleAddSymbol}
          />

          <TimeRangeTabs
            selected={selectedTab}
            onChange={handleTimeRangeChange}
          />
        </div>

        <ChartLegendSection
          tickerChartData={tickerChartData}
          selectedTab={selectedTab}
          isLoading={status === "pending"}
          isError={status === "error"}
          refetchData={refetch}
          tickerLegends={tickerLegends}
          tickersArr={tickersArr}
          handleTickerSelectionChange={handleTickerSelectionChange}
          handleTickerRemoval={handleTickerRemoval}
        />
      </div>
    </>
  );
}
